/**
 * useFinanceConnections — aggregator hook for the Finance hub picker.
 *
 * Finance is the surface with the most heterogeneous connection mix in the
 * product: a user can wire 1+ open-banking accounts (Intesa, Unicredit,
 * BancoPosta …), one Fatture in Cloud company per fiscal entity, one or
 * more Salesforce envs, plus the Italian compliance trio (Aruba SDI,
 * Acube Cassetto Fiscale, …). Every panel on the hub (Conti & Saldi,
 * Fatture, CRM pipeline, F24 scadenze) reads from a DIFFERENT capability,
 * so the v1 ``completeness.banking_connected`` boolean is no longer
 * descriptive — we need to know *which* connection the user is filtering
 * on, not just *whether* the slot is filled.
 *
 * Pattern parity with ``useCalendarConnections``: fans out the BE
 * ``GET /connectors/{capability}/connections`` route across the finance
 * capability set in parallel via ``useQueries``, then flattens the
 * results into a single typed array the SurfaceHeader picker can render.
 *
 * Per CLAUDE.md "white-label naming OBBLIGATORIO": every capability slug
 * is the *capability* (``open_banking`` / ``accounting`` / ``crm``),
 * never the vendor. The ``provider`` field on the returned
 * ``FinanceConnection`` carries the vendor slug pulled from
 * ``ConnectionSummary.vendor`` so the picker's brand-colour resolver
 * can paint each chip correctly (Salesforce blue, FiC orange, etc.).
 */

import {
  type CapabilityName,
  type ConnectionSummary,
  listCapabilityConnections,
} from "@ando/core";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";

/**
 * Capability subset that the Finance hub aggregates. Order is meaningful:
 * the picker lists Conti & Saldi first (banking is the user's "ground
 * truth" cash source), then fiscal/invoicing, then CRM / pipeline.
 *
 * Each entry maps to a stable Finance-surface ``kind`` so downstream
 * panels can filter by source type without re-deriving from the
 * capability slug.
 */
export type FinanceConnectionKind =
  | "open_banking"
  | "accounting"
  | "crm"
  | "sdi_invoicing"
  | "cassetto_fiscale";

const FINANCE_CAPABILITIES: readonly FinanceConnectionKind[] = [
  "open_banking",
  "accounting",
  "crm",
  "sdi_invoicing",
  "cassetto_fiscale",
];

/**
 * Flattened connection shape consumed by the Finance hub. Mirrors the
 * ``CalendarConnection`` contract but adds a ``capability`` discriminator
 * (``open_banking`` vs ``accounting`` vs ``crm`` …) since one hub mixes
 * heterogeneous sources.
 */
export interface FinanceConnection {
  /** Stable BE id — used as picker option id + localStorage entry. */
  readonly id: string;
  /** Vendor slug (``fatture_in_cloud`` / ``salesforce`` / ``intesa`` …). */
  readonly provider: string;
  /** Capability slot this connection fills inside the Finance hub. */
  readonly capability: FinanceConnectionKind;
  /** Human label — prefers ``connection_key`` (account label), falls back
   * to the vendor name when the BE didn't return one. */
  readonly account_label: string;
  /** ``token_status === "needs_reauth"`` — surfaced by the picker so
   * users notice broken accounts before they wonder why panels are empty. */
  readonly needs_reauth: boolean;
  /** ISO 8601 expiry of the REAL consent (PSD2: 180d, AdE delega
   * cassetto: 4y, …). Used by the shared ``<ConsentExpiryBanner>`` to
   * surface "renew soon" warnings 14/7/2 days before access lapses.
   * ``null`` for capabilities whose authorisation is just an
   * auto-refreshing OAuth refresh token (FiC accounting, Salesforce
   * CRM, SDI invoicing) — banner stays silent for those, which is
   * correct (the user never loses access without explicit revoke). */
  readonly consent_expires_at: string | null;
}

export interface UseFinanceConnectionsResult {
  readonly connections: readonly FinanceConnection[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
}

/**
 * Map one BE ``ConnectionSummary`` row onto the surface-flat
 * ``FinanceConnection``. Kept pure + exported-by-module so the unit test
 * (when one lands) can hit it without spinning a Query client.
 */
function summaryToFinanceConnection(
  summary: ConnectionSummary,
  capability: FinanceConnectionKind,
): FinanceConnection {
  // Best-effort label: prefer the human-readable connection_key (e.g. the
  // IBAN tail for banking, the FiC company name for accounting, the SF
  // org alias for CRM). Falls back to the vendor slug capitalised so the
  // picker dropdown never shows an empty row.
  //
  // ``vendor`` is declared as required in ``ConnectionSummary`` but the BE
  // OMITS it for some white-label rows (see field doc above). Defensive
  // chain prevents the white-screen crash spotted June 1, 2026
  // (FinanceHub → useFinanceConnections → summary.vendor.split on
  // undefined).
  const slug = summary.vendor || summary.provider || summary.capability || "";
  const fallback = slug
    ? slug
        .split(/[_-]/)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : i18nSingleton._(msg`Connessione`);
  return {
    id: summary.conn_id,
    // ``provider`` here is the FE-facing routing slug consumed by the
    // FinanceHub picker — keep the same fallback chain so the row is
    // always identifiable downstream.
    provider: slug,
    capability,
    account_label: summary.connection_key?.trim() || fallback,
    needs_reauth: summary.needs_reauth,
    // June 4 2026 — read the REAL consent expiry, not the OAuth access
    // token. ``ConnectionSummary.consent_expires_at`` is populated by the
    // BE only for PSD2 / AdE delega; ``null`` for every other capability
    // (and the banner stays silent — which is correct).
    consent_expires_at: summary.consent_expires_at ?? null,
  };
}

/**
 * Fan out across every Finance capability in parallel + flatten the
 * resulting connection rows into a single typed array.
 *
 * Capability gating: we DON'T short-circuit per-capability with
 * ``useHasCapability`` here because the integrations router already
 * grants the capability via license; the BE returns ``[]`` for
 * capabilities the tenant doesn't subscribe to, so the hook degrades
 * cleanly. We DO gate the umbrella ``finance`` capability when present
 * — a future BE flag — to avoid hammering 403s for users on a free tier.
 */
export function useFinanceConnections(): UseFinanceConnectionsResult {
  // Capability gating: ``"finance"`` isn't a BE capability today —
  // capabilities are per-connector. The BE returns ``[]`` for slots the
  // tenant doesn't subscribe to, so the hook degrades cleanly without
  // an umbrella gate. When the BE introduces one, wire ``enabled``
  // through ``useHasCapability("finance")`` here.
  const results = useQueries({
    queries: FINANCE_CAPABILITIES.map((capability) => ({
      queryKey: ["workspace", "finance", "connections", capability] as const,
      queryFn: () => listCapabilityConnections(capability as CapabilityName),
      // Match useCalendarConnections cadence — connection health rarely
      // shifts inside a 5-min window and we don't want to spam 5x
      // refetchInterval timers (one per capability).
      staleTime: 5 * 60_000,
      refetchInterval: 5 * 60_000,
    })),
  });

  // Flatten + tag each row with the capability that produced it. We
  // preserve the order of FINANCE_CAPABILITIES so the picker dropdown
  // groups banking first → accounting → CRM → fiscal automatically.
  const connections = useMemo<readonly FinanceConnection[]>(() => {
    const out: FinanceConnection[] = [];
    for (let i = 0; i < FINANCE_CAPABILITIES.length; i += 1) {
      const capability = FINANCE_CAPABILITIES[i]!;
      const rows = results[i]?.data;
      if (!rows) continue;
      for (const row of rows) {
        out.push(summaryToFinanceConnection(row, capability));
      }
    }
    return out;
  }, [results]);

  const isLoading = results.some((r) => r.isLoading);
  const isError = results.some((r) => r.isError);
  const error = results.find((r) => r.error)?.error ?? null;

  return {
    connections,
    isLoading,
    isError,
    error,
  };
}

// ---------------------------------------------------------------------------
// Brand-colour overrides used by the Finance ConnectionPicker.
//
// The picker's ``DEFAULT_PROVIDER_COLORS`` already covers ``salesforce``
// (#00A1E0). For the remaining finance vendors we keep the overrides
// here so the FE owns the brand mapping and tests can assert against
// stable hex values.
// ---------------------------------------------------------------------------

export const FINANCE_PROVIDER_COLORS: Readonly<Record<string, string>> = {
  // Fatture in Cloud — TeamSystem orange-red, distinct from Ando accent.
  fatture_in_cloud: "#E63946",
  // Aruba SDI / PEC family — Aruba green.
  aruba: "#5BA838",
  aruba_pec: "#5BA838",
  aruba_sdi: "#5BA838",
  // Fiscal capabilities (Cassetto Fiscale, SDI) — fiscal purple.
  sdi_invoicing: "#6D4AFF",
  cassetto_fiscale: "#6D4AFF",
  // Open-banking aggregators stay white-label; the picker degrades to
  // the Ando accent when no capability-specific entry exists. We pin a
  // calm green to the capability slot ``open_banking`` so users instantly
  // recognise "banca collegata" — distinct from Salesforce blue. After
  // the white-label BE refactor (2026-05-30) the connection ``provider``
  // field is always the capability key, never the vendor slug.
  open_banking: "#2E7D32",
  // HubSpot orange — distinguishable from Ando accent (which is closer
  // to red-orange).
  hubspot: "#FF7A59",
};

/**
 * Helper — map a Finance capability kind onto a coarse "section" the UI
 * panels can switch on. Keeps the panel-filter logic readable without
 * exporting a 5-way ternary at every call site.
 */
export function financeSectionOf(
  kind: FinanceConnectionKind,
): "banking" | "fiscal" | "crm" {
  switch (kind) {
    case "open_banking":
      return "banking";
    case "accounting":
    case "sdi_invoicing":
    case "cassetto_fiscale":
      return "fiscal";
    case "crm":
      return "crm";
  }
}
